import React, { useState, useMemo } from 'react';

const DateMonthGrid = ({
  dateRange,
  filters,
  validDaysOfWeek,
  validDates,
  handleFilterChange,
  getMonthDateGrids
}) => {
  const dowShort = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const dowFull = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  // --- New: Compute all months and keep track of visible month index ---
  const allMonths = useMemo(() => {
    // Get unique months in order from dateRange
    const monthMap = dateRange.reduce((acc, date) => {
      const month = new Date(date).toLocaleString('en-US', { month: 'short', year: 'numeric' }).toUpperCase();
      if (!acc.includes(month)) acc.push(month);
      return acc;
    }, []);
    return monthMap;
  }, [dateRange]);

  const [visibleMonthIdx, setVisibleMonthIdx] = useState(0);

  // --- Only show the selected month in the grid ---
  const monthDateGrids = useMemo(() => {
    // Get all month grids, but only show the one at visibleMonthIdx
    const allGrids = getMonthDateGrids(dateRange, true); // get all months
    if (allGrids.length === 0) return [];
    return [allGrids[visibleMonthIdx]];
  }, [dateRange, getMonthDateGrids, visibleMonthIdx]);

  return (
    <div className="flex flex-row items-center w-full justify-center mb-1">
      {/* Left arrow (previous month) */}
      <button
        onClick={() => setVisibleMonthIdx(idx => Math.max(0, idx - 1))}
        disabled={visibleMonthIdx === 0}
        className={`p-1 rounded-full border mr-2 ${visibleMonthIdx === 0 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
        style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        aria-label="Previous Month"
      >
        {/* Chevron Left */}
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
          <path d="M13 16l-5-6 5-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {/* Date grid and month label */}
      {monthDateGrids.map(({ month, weeks }, monthIdx) => (
        <div
          key={month}
          className="flex flex-row items-start mb-2 justify-center w-full"
        >
          {/* Rotated month label on the left */}
          <div className="flex flex-col justify-center items-center mr-2">
            <span
              className="text-xs font-bold text-gray-500"
              style={{
                writingMode: 'vertical-rl',
                transform: 'rotate(180deg)',
                whiteSpace: 'nowrap',
                letterSpacing: '0.1em',
                minHeight: '12.5rem', // 5 rows * 2.5rem, matches the grid height
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {month}
            </span>
          </div>
          {/* Day of week header and date grid */}
          <div>
            <div className="grid grid-cols-7 gap-1 mb-1">
              {dowShort.map((label, i) => {
                const fullDay = dowFull[i];
                const isSelected = (filters.dayOfWeek || []).includes(fullDay);
                const isValid = validDaysOfWeek.has(fullDay);
                return (
                  <label
                    key={month + '-dow-' + i}
                    className={`w-7 h-7 flex items-center justify-center border rounded cursor-pointer text-xs
                      ${isSelected ? 'bg-blue-500 text-white' : isValid ? 'bg-gray-100 hover:bg-gray-200' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}
                    `}
                    title={fullDay}
                    style={{ fontStyle: 'italic' }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleFilterChange('dayOfWeek', fullDay)}
                      className="hidden"
                      disabled={!isValid && !isSelected}
                    />
                    {label}
                  </label>
                );
              })}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {(() => {
                // Flatten weeks to a single array of 35 cells (7x5 grid)
                const flatCells = [];
                for (let wi = 0; wi < 5; wi++) {
                  const week = weeks[wi] || [];
                  for (let di = 0; di < 7; di++) {
                    const date = week[di] || null;
                    if (!date) {
                      flatCells.push(
                        <div key={month + '-empty-' + wi + '-' + di} className="w-7 h-7 flex items-center justify-center" />
                      );
                    } else {
                      const dateObj = new Date(date);
                      const isToday = date === new Date().toISOString().split('T')[0];
                      const isSelected = (filters.dates || []).includes(date);
                      const isValid = validDates.has(date);
                      flatCells.push(
                        <label
                          key={month + '-' + date}
                          className={`w-7 h-7 flex items-center justify-center border rounded cursor-pointer text-xs
                            ${isSelected ? 'bg-blue-500 text-white' : isValid ? 'bg-gray-100 hover:bg-gray-200' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}
                            ${isToday ? 'font-bold border-blue-400' : ''}
                          `}
                          title={dateObj.toLocaleDateString()}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleFilterChange('dates', date)}
                            className="hidden"
                            disabled={!isValid && !isSelected}
                          />
                          {dateObj.getDate()}
                        </label>
                      );
                    }
                  }
                }
                return flatCells;
              })()}
            </div>
          </div>
        </div>
      ))}
      {/* Right arrow (next month) */}
      <button
        onClick={() => setVisibleMonthIdx(idx => Math.min(allMonths.length - 1, idx + 1))}
        disabled={visibleMonthIdx === allMonths.length - 1}
        className={`p-1 rounded-full border ml-2 ${visibleMonthIdx === allMonths.length - 1 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
        style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        aria-label="Next Month"
      >
        {/* Chevron Right */}
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
          <path d="M7 4l5 6-5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    </div>
  );
};

export default DateMonthGrid;
